import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { useBlockBrowserClose } from '@/hooks/useBlockBrowserClose';
import ErrorIcon from '@/images/Restart.svg';
import SavedIcon from '@/images/autoSaveStatus/Saved.svg';
import SavingIcon from '@/images/autoSaveStatus/Saving.svg';
import styled from '@emotion/styled';
import { IconButton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useAdvancedEditorContext } from '../../context/useAdvancedEditorContext';
import { getStoreHash, getStoreProperty, PolotnoStoreKeys } from '../../hooks';
import { ProjectAutoSaveStatus } from '../../types';

const StyledIcon = styled(IconButton)`
    right: 4px;
    padding: 4px;
    border-radius: 12px;
`;

export const StyledText = styled(Typography)`
    margin-left: 8px;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.28px;
`;

const StatusToIcon: Record<ProjectAutoSaveStatus, string> = {
    [ProjectAutoSaveStatus.Idle]: SavedIcon,
    [ProjectAutoSaveStatus.Saved]: SavedIcon,
    [ProjectAutoSaveStatus.Saving]: SavingIcon,
    [ProjectAutoSaveStatus.Error]: ErrorIcon,
};

const StatusToLabel: Record<ProjectAutoSaveStatus, string> = {
    [ProjectAutoSaveStatus.Idle]: '',
    [ProjectAutoSaveStatus.Saved]: 'Changes saved',
    [ProjectAutoSaveStatus.Saving]: 'Saving...',
    [ProjectAutoSaveStatus.Error]: 'Unsaved',
};

export const AutoSaveStatus = observer(() => {
    const { store, saveArtifacts, hash } = useAdvancedEditorContext();

    const projectAutoSaveStatus = getStoreProperty(store, PolotnoStoreKeys.projectAutoSaveStatus);
    const theme = useTheme();

    const color = projectAutoSaveStatus === ProjectAutoSaveStatus.Error ? theme.colors.error[750] : theme.colors.black[500];
    const title = projectAutoSaveStatus === ProjectAutoSaveStatus.Error ? 'Unsaved changes. Click to retry.' : '';

    const handleClick = () => saveArtifacts(store);

    const checkIfShouldPreventUnload = () => {
        const wrongStatus = [ProjectAutoSaveStatus.Saving, ProjectAutoSaveStatus.Error].includes(projectAutoSaveStatus);

        const currentHash = getStoreHash(store);
        const differentHash = hash !== currentHash;

        return wrongStatus || differentHash;
    };

    useBlockBrowserClose(checkIfShouldPreventUnload, () => {
        saveArtifacts(store);
    });

    return (
        <CustomTooltip title={title}>
            <StyledIcon onClick={handleClick} disabled={projectAutoSaveStatus !== ProjectAutoSaveStatus.Error}>
                <MaskedIcon color={color} src={StatusToIcon[projectAutoSaveStatus]} size="20px" />
                <StyledText color={color}>{StatusToLabel[projectAutoSaveStatus]}</StyledText>
            </StyledIcon>
        </CustomTooltip>
    );
});
